import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchService from '../../services/search.services';
import { PAGINATION_CONFIG } from '../../config/paginationConfig';

/**
 * Page sizes offered by the searches table. The backend clamps `limit` at 500,
 * so every option here is served in a single request.
 */
export const SEARCH_PAGE_SIZE_OPTIONS = PAGINATION_CONFIG.PAGE_SIZE_OPTIONS;
export const DEFAULT_SEARCH_PAGE_SIZE = 100;

/**
 * `GET /searches/:companyid` pages by cursor (`limit` + `sinceId`), not by
 * offset: a page is addressed by the id of the last row before it, and the
 * response never reports a total. That shapes everything below.
 *
 *   - Cursors are discovered by walking forward, so page N is only reachable
 *     once page N-1 has been fetched. `cursorsRef` is that trail of
 *     breadcrumbs — index i holds the `sinceId` that opens page i — which is
 *     what makes "previous page" and "first page" instant re-fetches rather
 *     than a re-walk.
 *   - `rowCount` is an estimate, because no total exists. It is what the table
 *     uses to decide whether "next" is enabled, so it counts the rows seen so
 *     far plus one when the backend says there is more.
 *   - Changing the page size invalidates every cursor (they address positions
 *     within a given page size), so the trail is dropped and paging restarts.
 *
 * One page in, one request out: the table never holds the whole company.
 *
 * @param {string} companyId
 * @param {object} [options]
 * @param {boolean} [options.enabled] Skip fetching until the tab is shown.
 * @param {object} [options.cache] Snapshot from a previous mount, so returning
 *        to the tab restores the page the user was on without a refetch.
 * @param {Function} [options.setCache] Stores the snapshot above.
 */
export const useSearchPages = (companyId, { enabled = true, cache, setCache } = {}) => {
	// Only consulted by the lazy initialisers below, i.e. on the first render.
	const restored = cache && cache.companyId === companyId ? cache : null;

	const [pagination, setPagination] = useState(() => ({
		pageIndex: restored?.pageIndex ?? 0,
		pageSize: restored?.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE,
	}));
	const [rows, setRows] = useState(() => restored?.rows ?? []);
	const [hasMore, setHasMore] = useState(() => restored?.hasMore ?? false);
	const [isFetching, setIsFetching] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(() => Boolean(restored));
	const [error, setError] = useState(null);
	const [reloadToken, setReloadToken] = useState(0);

	const cursorsRef = useRef(restored ? [...restored.cursors] : [null]);
	// Identifies the page currently held in state, so re-renders and tab
	// switches do not re-request what is already on screen.
	const loadedKeyRef = useRef(
		restored ? `${companyId}|${restored.pageSize}|${restored.pageIndex}|0` : null
	);

	const { pageIndex, pageSize } = pagination;

	// A different company is a different list: its cursors mean nothing here,
	// so paging starts over rather than resuming at whatever page was open.
	const companyRef = useRef(companyId);
	useEffect(() => {
		if (companyRef.current === companyId) return;
		companyRef.current = companyId;
		cursorsRef.current = [null];
		loadedKeyRef.current = null;
		setRows([]);
		setHasMore(false);
		setHasLoaded(false);
		setError(null);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, [companyId]);

	useEffect(() => {
		if (!enabled || !companyId) return undefined;

		const key = `${companyId}|${pageSize}|${pageIndex}|${reloadToken}`;
		if (loadedKeyRef.current === key) return undefined;

		const sinceId = cursorsRef.current[pageIndex];
		if (sinceId === undefined) {
			// No cursor recorded for this page — a jump past the pages walked so
			// far. Fall back to the furthest page we can actually address.
			setPagination((prev) => ({ ...prev, pageIndex: cursorsRef.current.length - 1 }));
			return undefined;
		}

		let cancelled = false;
		setIsFetching(true);

		(async () => {
			try {
				const response = await SearchService.getSearches(companyId, {
					limit: pageSize,
					sinceId,
				});
				if (cancelled) return;

				const items = Array.isArray(response?.data) ? response.data : [];
				const paging = response?.paging;
				const more = Boolean(paging?.hasMore && paging?.nextSinceId);

				if (more) {
					cursorsRef.current[pageIndex + 1] = paging.nextSinceId;
				} else {
					// Last page: forget any cursors recorded beyond it, so a stale
					// trail from a longer list cannot be paged into.
					cursorsRef.current.length = pageIndex + 1;
				}

				setRows(items);
				setHasMore(more);
				setError(null);
				setHasLoaded(true);
				loadedKeyRef.current = key;
				setCache?.({
					companyId,
					pageIndex,
					pageSize,
					cursors: [...cursorsRef.current],
					rows: items,
					hasMore: more,
				});
			} catch (err) {
				if (cancelled) return;
				// loadedKeyRef is left untouched so the same page is retried.
				setError(err?.message || 'Unknown error');
			} finally {
				if (!cancelled) setIsFetching(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [companyId, enabled, pageIndex, pageSize, reloadToken, setCache]);

	/**
	 * Pagination handler for the table. Takes TanStack's updater-or-value.
	 */
	const onPaginationChange = useCallback((updaterOrValue) => {
		setPagination((prev) => {
			const next =
				typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
			if (next.pageSize !== prev.pageSize) {
				cursorsRef.current = [null];
				return { pageIndex: 0, pageSize: next.pageSize };
			}
			return next;
		});
	}, []);

	/** Drops the cached page and re-requests the first one. */
	const refresh = useCallback(() => {
		cursorsRef.current = [null];
		loadedKeyRef.current = null;
		setCache?.(null);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
		setReloadToken((token) => token + 1);
	}, [setCache]);

	// No total is available, so this is "everything seen so far, plus one more
	// if the backend says the list continues" — enough for the table to know
	// whether the next page exists.
	const rowCount = pageIndex * pageSize + rows.length + (hasMore ? 1 : 0);

	const value = useMemo(
		() => ({
			rows,
			hasLoaded,
			pagination,
			onPaginationChange,
			rowCount,
			hasMore,
			// The first fetch has nothing to show behind it; later ones keep the
			// current page visible under a progress bar.
			isLoading: isFetching && !hasLoaded,
			isFetching,
			error,
			refresh,
			pageSizeOptions: SEARCH_PAGE_SIZE_OPTIONS,
		}),
		[rows, pagination, onPaginationChange, rowCount, hasMore, isFetching, hasLoaded, error, refresh]
	);

	return value;
};

export default useSearchPages;
